<template>
    <nav
        :class="[$style['mds-tabs'], classObject, responsiveObject, variationClass, sizeClass]"
        :data-mds-version="version"
    >
        <ul ref="container" role="tablist" :class="$style['mds-tabs__inner']">
            <slot>
                <mds-tabs-item
                    v-for="(item, index) in content"
                    :onDark="onDark"
                    :key="index"
                    :size="size"
                    v-bind="item"
                    v-on:mds-tabs-item-active="$emit('mds-tabs-item-active', $event)"
                >
                </mds-tabs-item>
            </slot>
        </ul>
    </nav>
</template>

<script>
    import MdsTabsItem from './tabs-item.vue';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsTabs',
        components: {
            MdsTabsItem,
        },
        props: {
            alignment: {
                type: String,
                default: 'left',
                validator(value) {
                    return ['left', 'center'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `left`, `center`.',
                    description: 'Sets the alignment of the tabs items.',
                },
            },
            content: {
                type: Array,
                required: false,
                default: () => [],
                validator(items) {
                    let passValidation = true;
                    items.forEach(item => {
                        if (item.hasOwnProperty('active') !== true) {
                            passValidation = false;
                        }
                    });
                    return passValidation;
                },
                docs: {
                    validation: '—',
                    description:
                        'Sets content for the tabs using the tabs item API defined below. **This prop has been deprecated. Use the `MdsTabs` default slot to pass `MdsTabsItem` instead.**{.mds-doc-prop-table__deprecated-text}',
                    deprecated: true,
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            responsive: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, turns on the overflow responsiveness of tabs. Implementing teams also need to call the `responsiveTabs()` method in order to trigger the responsive behavior.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the containted tabs items.',
                },
            },
            variation: {
                type: String,
                default: 'in-page',
                validator(value) {
                    return ['in-page', 'site-navigation'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `in-page`, `site-navigation`.',
                    description: 'Sets the tabs variation.',
                },
            },
        },
        provide() {
            // Properties that are available for injection into components descendants https://vuejs.org/v2/api/#provide-inject
            const parentVariation = {};
            Object.defineProperty(parentVariation, 'onDark', {
                enumerable: true,
                get: () => this.onDark,
            });
            Object.defineProperty(parentVariation, 'size', {
                enumerable: true,
                get: () => this.size,
            });
            return { parentVariation };
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-tabs--${this.size}`];
            },
            variationClass() {
                return this.$style[`mds-tabs--${this.variation}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-tabs--centered']] = this.alignment === 'center';
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            responsiveObject() {
                const obj = {};
                obj[this.$style['mds-tabs--responsive']] = this.responsive;
                obj[this.$style['mds-tabs--responsive-left']] = this.responsive && this.overflowLeft;
                obj[this.$style['mds-tabs--responsive-right']] = this.responsive && this.overflowRight;

                return obj;
            },
        },
        watch: {
            responsive(newVal) {
                if (newVal) {
                    this.responsiveTabs();
                }
            },
        },
        data() {
            return {
                overflowRight: false,
                overflowLeft: false,
                version: `${name}-${version}`,
            };
        },
        methods: {
            responsiveTabs() {
                const containerScrollWidth = this.containerEl.scrollWidth;
                const containerOffsetWidth = this.containerEl.offsetWidth;
                const containerScrollLeft = Math.round(this.containerEl.scrollLeft);
                const containerScrollLeftMax = this.containerEl.scrollWidth - this.containerEl.clientWidth;
                this.overflowRight = containerScrollWidth > containerOffsetWidth;
                this.overflowLeft = containerScrollLeft > 0;
                this.overflowRight = containerScrollLeft !== containerScrollLeftMax;
                this.$refs['container'].addEventListener('scroll', this.responsiveTabs);
            },
        },
        mounted() {
            this.containerEl = this.$refs['container'];
            if (this.responsive) {
                this.responsiveTabs();
            }
        },
    };
</script>

<style lang="scss" module>
    @import './tabs.scss';
</style>
